import { useSeoMeta } from '@unhead/react';
import { Dices } from 'lucide-react';
import { useTombolo, STEP_ORDER, type TomboloStep } from '@/hooks/useTombolo';
import { Stepper } from '@/components/tombolo/Stepper';
import { StepNotes } from '@/components/tombolo/StepNotes';
import { StepRules } from '@/components/tombolo/StepRules';
import { StepParticipants } from '@/components/tombolo/StepParticipants';
import { StepFilter } from '@/components/tombolo/StepFilter';
import { StepTickets } from '@/components/tombolo/StepTickets';
import { StepDraw } from '@/components/tombolo/StepDraw';

const Index = () => {
  useSeoMeta({
    title: 'Nostr Tombolo — Fair Nostr Raffles',
    description:
      'Run fair, transparent raffles on Nostr. Paste note IDs, fetch real engagement, filter out bots, assign tickets and draw a winner.',
  });

  const state = useTombolo();
  const { step, setStep, isLocked, noteIds, engagement, signals, eligible, finalized } = state;

  // The furthest step the user can jump to, based on real progress
  const maxReached: TomboloStep = finalized
    ? 'draw'
    : signals && eligible.length > 0
      ? 'tickets'
      : engagement
        ? 'filter'
        : noteIds.length > 0
          ? 'rules'
          : 'notes';

  // Never render a step beyond what's allowed
  const effectiveStep = STEP_ORDER.indexOf(step) <= STEP_ORDER.indexOf(maxReached) ? step : maxReached;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/40">
      <div className="container max-w-3xl px-4 sm:px-8 py-8 sm:py-12 space-y-8">
        {/* Header */}
        <header className="text-center space-y-3">
          <div className="inline-flex items-center justify-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
              <Dices className="h-7 w-7" aria-hidden />
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Nostr Tombolo</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            A fair, transparent raffle for your Nostr notes. One person, one
            ticket — bots filtered out, everything visible.
          </p>
        </header>

        {/* Progress */}
        <Stepper
          current={effectiveStep}
          maxReached={maxReached}
          onNavigate={setStep}
          isLocked={isLocked}
        />

        {/* Active step */}
        <main>
          {effectiveStep === 'notes' && <StepNotes state={state} />}
          {effectiveStep === 'rules' && <StepRules state={state} />}
          {effectiveStep === 'participants' && <StepParticipants state={state} />}
          {effectiveStep === 'filter' && <StepFilter state={state} />}
          {effectiveStep === 'tickets' && <StepTickets state={state} />}
          {effectiveStep === 'draw' && <StepDraw state={state} />}
        </main>

        {/* Fairness footer */}
        <footer className="text-center space-y-4 pt-4">
          <div className="text-sm text-muted-foreground max-w-lg mx-auto space-y-1">
            <p className="font-medium text-foreground">How fairness works</p>
            <p>
              One npub = one ticket, no matter how many actions or notes.
              Tickets are locked before the draw. The winner is simply the
              ticket closest to your drawn number, and every filtering decision
              is shown with its reason.
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              Vibed with Shakespeare
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Index;
